import React, { useMemo, useState } from 'react';
import { Calculator, CheckCircle2, XCircle, Sigma, ListChecks, Scale, NotebookPen, Download, ClipboardCheck, ShieldCheck, Target } from 'lucide-react';
import './LessonTools.css';

// Interactive lesson sections used by the applied finance courses: calculators,
// auto-marked practice questions, mini-case decisions, classification
// exercises, reflections, downloads, diagnostics, formula and worked-example
// cards, and versioned regulatory notes. Each takes the section object from
// the course data (see scripts/finance-courses/CONTENT_CONTRACT.md).

/* ─── Number formatting ─── */
const group = (n, dp = 0) => {
  if (!Number.isFinite(n)) return '–';
  const neg = n < 0;
  const [int, dec] = Math.abs(n).toFixed(dp).split('.');
  const body = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${neg ? '−' : ''}${body}${dec ? '.' + dec : ''}`;
};
const fmt = {
  R: n => (Number.isFinite(n) ? `${n < 0 ? '−' : ''}R${group(Math.abs(n))}` : '–'),
  pct: (n, dp = 1) => (Number.isFinite(n) ? `${group(n, dp)}%` : '–'),
  x: n => (Number.isFinite(n) ? `${group(n, 2)}x` : '–'),
  days: n => (Number.isFinite(n) ? `${group(n, 1)} days` : '–'),
  num: (n, dp = 0) => group(n, dp),
  years: n => (Number.isFinite(n) ? `${group(n, 1)} years` : '–'),
};
const div = (a, b) => (b ? a / b : NaN);
const annuity = (P, rPct, n) => {
  const r = rPct / 100;
  if (n <= 0) return NaN;
  return r === 0 ? P / n : P * r / (1 - Math.pow(1 + r, -n));
};
const pvAnnuity = (pmt, rPct, n) => {
  const r = rPct / 100;
  return r === 0 ? pmt * n : pmt * (1 - Math.pow(1 + r, -n)) / r;
};
function irr(flows) {
  const npvAt = r => flows.reduce((s, f, t) => s + f / Math.pow(1 + r, t), 0);
  let lo = -0.99, hi = 5;
  if (npvAt(lo) * npvAt(hi) > 0) return NaN;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    if (npvAt(lo) * npvAt(mid) <= 0) hi = mid; else lo = mid;
  }
  return ((lo + hi) / 2) * 100;
}
function schedule(principal, rate, years, { grace = 0, structure = 0, balloonPct = 0 } = {}) {
  const rows = [];
  let bal = principal;
  const r = rate / 100;
  const amortYears = Math.max(years - grace, 0);
  const balloon = structure === 3 ? principal * balloonPct / 100 : 0;
  for (let y = 1; y <= years; y++) {
    const interest = bal * r;
    let principalPaid = 0;
    if (y > grace) {
      if (structure === 2) principalPaid = y === years ? bal : 0;
      else if (structure === 1) principalPaid = principal / amortYears;
      else if (structure === 3) {
        const pmt = r === 0 ? (principal - balloon) / amortYears
          : (principal - balloon / Math.pow(1 + r, amortYears)) * r / (1 - Math.pow(1 + r, -amortYears));
        principalPaid = y === years ? bal : pmt - interest;
      } else {
        principalPaid = annuity(principal, rate, amortYears) - interest;
        if (y === years) principalPaid = bal;
      }
    }
    principalPaid = Math.min(principalPaid, bal);
    rows.push({ y, opening: bal, interest, principal: principalPaid, total: interest + principalPaid, closing: bal - principalPaid });
    bal -= principalPaid;
  }
  return rows;
}
const scheduleTable = rows => ({
  headers: ['Year', 'Opening', 'Interest', 'Principal', 'Total payment', 'Closing'],
  rows: rows.map(r => [String(r.y), fmt.R(r.opening), fmt.R(r.interest), fmt.R(r.principal), fmt.R(r.total), fmt.R(r.closing)]),
});

/* ─── Calculator registry ─── */
const R = (key, label, def = 0) => ({ key, label, unit: 'R', def });
const P = (key, label, def = 0) => ({ key, label, unit: '%', def, step: 0.1 });
const N = (key, label, def = 0, unit = '', step = 1) => ({ key, label, unit, def, step });
const S = (key, label, options, def = 0) => ({ key, label, options, def });

const CALCULATORS = {
  'accounting-equation': {
    title: 'Accounting equation',
    inputs: [R('assets', 'Total assets', 1500000), R('liabilities', 'Total liabilities', 900000)],
    compute: v => ({ rows: [['Equity (Assets − Liabilities)', fmt.R(v.assets - v.liabilities)]], note: 'Assets = Liabilities + Equity, so Equity = Assets − Liabilities.' }),
  },
  'gross-margin': {
    title: 'Gross margin',
    inputs: [R('revenue', 'Revenue', 10000000), R('costOfSales', 'Cost of sales', 6500000)],
    compute: v => { const gp = v.revenue - v.costOfSales; return { rows: [['Gross profit', fmt.R(gp)], ['Gross margin', fmt.pct(div(gp, v.revenue) * 100)]] }; },
  },
  margins: {
    title: 'Margin analysis',
    inputs: [R('revenue', 'Revenue', 27600000), R('costOfSales', 'Cost of sales', 18216000), R('operatingExpenses', 'Operating expenses (excl. depreciation)', 6350000), R('depreciation', 'Depreciation', 780000), R('interest', 'Finance costs', 610000), R('tax', 'Income tax', 444000)],
    compute: v => {
      const gp = v.revenue - v.costOfSales, ebitda = gp - v.operatingExpenses, ebit = ebitda - v.depreciation, np = ebit - v.interest - v.tax;
      const m = x => fmt.pct(div(x, v.revenue) * 100);
      return { table: { headers: ['Measure', 'Amount', 'Margin'], rows: [['Gross profit', fmt.R(gp), m(gp)], ['EBITDA', fmt.R(ebitda), m(ebitda)], ['Operating profit (EBIT)', fmt.R(ebit), m(ebit)], ['Net profit', fmt.R(np), m(np)]] } };
    },
  },
  'current-ratio': {
    title: 'Current ratio',
    inputs: [R('currentAssets', 'Current assets', 5208000), R('currentLiabilities', 'Current liabilities', 3372000)],
    compute: v => ({ rows: [['Current ratio', fmt.x(div(v.currentAssets, v.currentLiabilities))]], note: 'A ratio is a question, not a score: ask how quickly current assets turn into cash.' }),
  },
  'quick-ratio': {
    title: 'Quick ratio',
    inputs: [R('cash', 'Cash', 0), R('receivables', 'Trade receivables', 2813000), R('currentLiabilities', 'Current liabilities', 3372000)],
    compute: v => ({ rows: [['Quick ratio', fmt.x(div(v.cash + v.receivables, v.currentLiabilities))]], note: 'Excludes inventory, which may take months to become cash.' }),
  },
  'working-capital-days': {
    title: 'Working capital days',
    inputs: [R('creditSales', 'Credit sales', 16560000), R('costOfSales', 'Cost of sales', 18216000), R('receivables', 'Closing trade receivables', 2813000), R('inventory', 'Closing inventory', 2396000), R('payables', 'Closing trade payables', 1747000)],
    compute: v => {
      const dd = div(v.receivables, v.creditSales) * 365, id = div(v.inventory, v.costOfSales) * 365, cd = div(v.payables, v.costOfSales) * 365;
      return { rows: [['Debtor days', fmt.days(dd)], ['Inventory days', fmt.days(id)], ['Creditor days', fmt.days(cd)], ['Cash conversion cycle', fmt.days(dd + id - cd)]] };
    },
  },
  'working-capital-simulator': {
    title: 'Working capital simulator',
    slider: true,
    inputs: [R('creditSales', 'Credit sales (annual)', 16560000), R('costOfSales', 'Cost of sales (annual)', 18216000),
      { ...N('debtorDays', 'Debtor days', 62, 'days'), min: 0, max: 150 }, { ...N('inventoryDays', 'Inventory days', 48, 'days'), min: 0, max: 150 }, { ...N('creditorDays', 'Creditor days', 35, 'days'), min: 0, max: 150 }],
    compute: (v, base) => {
      const tied = x => x.creditSales * x.debtorDays / 365 + x.costOfSales * x.inventoryDays / 365 - x.costOfSales * x.creditorDays / 365;
      const now = tied(v), was = tied(base), delta = was - now;
      return {
        rows: [['Receivables', fmt.R(v.creditSales * v.debtorDays / 365)], ['Inventory', fmt.R(v.costOfSales * v.inventoryDays / 365)], ['Trade payables', fmt.R(v.costOfSales * v.creditorDays / 365)],
          ['Cash conversion cycle', fmt.days(v.debtorDays + v.inventoryDays - v.creditorDays)], ['Net working capital tied up', fmt.R(now)],
          [delta >= 0 ? 'Cash released vs starting position' : 'Extra cash absorbed vs starting position', fmt.R(Math.abs(delta))]],
        verdict: Math.abs(delta) < 1 ? null : (delta > 0 ? 'good' : 'bad'),
      };
    },
  },
  'break-even': {
    title: 'Contribution and break-even',
    inputs: [R('sellingPrice', 'Selling price per unit', 2000), R('variableCost', 'Variable cost per unit', 1200), R('fixedCosts', 'Fixed costs', 2400000), N('targetUnits', 'Planned units', 3500, 'units')],
    compute: v => {
      const c = v.sellingPrice - v.variableCost, be = div(v.fixedCosts, c);
      return { rows: [['Contribution per unit', fmt.R(c)], ['Contribution margin', fmt.pct(div(c, v.sellingPrice) * 100)], ['Break-even units', `${fmt.num(Math.ceil(be))} units`], ['Break-even revenue', fmt.R(Math.ceil(be) * v.sellingPrice)],
        ['Margin of safety', `${fmt.num(v.targetUnits - Math.ceil(be))} units (${fmt.pct(div(v.targetUnits - be, v.targetUnits) * 100)})`], ['Profit at planned units', fmt.R(v.targetUnits * c - v.fixedCosts)]] };
    },
  },
  payback: {
    title: 'Payback period',
    inputs: [R('investment', 'Initial investment', 1000000), R('y1', 'Year 1 net cash inflow', 300000), R('y2', 'Year 2', 300000), R('y3', 'Year 3', 300000), R('y4', 'Year 4', 300000), R('y5', 'Year 5', 300000)],
    compute: v => {
      let cum = -v.investment, pb = NaN;
      const rows = [];
      [v.y1, v.y2, v.y3, v.y4, v.y5].forEach((f, i) => {
        const prev = cum; cum += f;
        if (Number.isNaN(pb) && cum >= 0 && f > 0) pb = i + (-prev / f);
        rows.push([`Cumulative cash, end of year ${i + 1}`, fmt.R(cum)]);
      });
      return { rows: [...rows, ['Payback period', Number.isNaN(pb) ? 'Not within five years' : fmt.years(pb)]], note: 'Payback ignores the time value of money and everything after the payback point.' };
    },
  },
  npv: {
    title: 'Net present value and IRR',
    inputs: [R('investment', 'Initial investment', 1000000), P('rate', 'Discount rate (training assumption)', 12), R('y1', 'Year 1 net cash inflow', 300000), R('y2', 'Year 2', 300000), R('y3', 'Year 3', 300000), R('y4', 'Year 4', 300000), R('y5', 'Year 5', 300000)],
    compute: v => {
      const flows = [v.y1, v.y2, v.y3, v.y4, v.y5];
      const pv = flows.map((f, i) => f / Math.pow(1 + v.rate / 100, i + 1));
      const npv = pv.reduce((a, b) => a + b, 0) - v.investment;
      return { table: { headers: ['Year', 'Cash flow', 'Present value'], rows: [['0', fmt.R(-v.investment), fmt.R(-v.investment)], ...flows.map((f, i) => [String(i + 1), fmt.R(f), fmt.R(pv[i])])] },
        rows: [['Net present value', fmt.R(npv)], ['IRR (approximate)', fmt.pct(irr([-v.investment, ...flows]))]], verdict: npv >= 0 ? 'good' : 'bad' };
    },
  },
  'budget-variance': {
    title: 'Budget variance',
    inputs: [R('budget', 'Budget', 500000), R('actual', 'Actual', 560000), S('costLine', 'Line type', [['1', 'Cost or expense line'], ['0', 'Revenue or income line']], 1)],
    compute: v => {
      const d = v.actual - v.budget, fav = Number(v.costLine) === 1 ? d <= 0 : d >= 0;
      return { rows: [['Variance (actual − budget)', fmt.R(d)], ['Variance %', fmt.pct(div(d, v.budget) * 100)], ['Direction', d === 0 ? 'On budget' : fav ? 'Favourable' : 'Adverse']], verdict: d === 0 ? null : fav ? 'good' : 'bad',
        note: 'Favourable or adverse is only the start: ask what changed, why, whether it is temporary or structural, and what action follows.' };
    },
  },
  leverage: {
    title: 'Leverage ratios',
    inputs: [R('totalDebt', 'Total interest-bearing debt (incl. overdraft)', 10690000), R('cash', 'Cash', 0), R('ebitda', 'EBITDA', 10325000), R('equity', 'Total equity', 20446000)],
    compute: v => ({ rows: [['Debt / EBITDA', fmt.x(div(v.totalDebt, v.ebitda))], ['Net debt / EBITDA', fmt.x(div(v.totalDebt - v.cash, v.ebitda))], ['Debt / equity', fmt.x(div(v.totalDebt, v.equity))]] }),
  },
  'interest-cover': {
    title: 'Interest cover',
    inputs: [R('ebitda', 'EBITDA', 10325000), R('financeCosts', 'Finance costs', 1420000)],
    compute: v => ({ rows: [['Interest cover (EBITDA ÷ finance costs)', fmt.x(div(v.ebitda, v.financeCosts))]], note: 'Interest cover ignores principal repayments. DSCR does not.' }),
  },
  'normalised-ebitda': {
    title: 'Normalised EBITDA',
    inputs: [R('reportedEbitda', 'Reported EBITDA', 12015000), R('onceOffGains', 'Once-off gains to remove', 1200000), R('onceOffCosts', 'Once-off costs to add back', 450000),
      R('ownerPayAdjustment', 'Owner remuneration adjustment (signed)', -700000), R('relatedPartyAdjustment', 'Related-party adjustment (signed)', -240000)],
    compute: v => {
      const n = v.reportedEbitda - v.onceOffGains + v.onceOffCosts + v.ownerPayAdjustment + v.relatedPartyAdjustment;
      return { table: { headers: ['Step', 'Amount'], rows: [['Reported EBITDA', fmt.R(v.reportedEbitda)], ['Less: once-off gains', fmt.R(-v.onceOffGains)], ['Add: once-off costs', fmt.R(v.onceOffCosts)], ['Owner remuneration to market', fmt.R(v.ownerPayAdjustment)], ['Related-party charges to market', fmt.R(v.relatedPartyAdjustment)], ['Normalised EBITDA', fmt.R(n)]] },
        note: 'Every adjustment must be identified, evidenced, explained and reversible if challenged.' };
    },
  },
  cfads: {
    title: 'Cash flow available for debt service (training model)',
    inputs: [R('ebitda', 'EBITDA (normalised)', 10325000), R('taxPaid', 'Tax paid', 2128000), R('workingCapitalInvestment', 'Investment in working capital', 1400000), R('maintenanceCapex', 'Maintenance capex', 2100000), R('otherAdjustments', 'Other lender adjustments (signed)', 0)],
    compute: v => {
      const c = v.ebitda - v.taxPaid - v.workingCapitalInvestment - v.maintenanceCapex + v.otherAdjustments;
      return { table: { headers: ['Bridge', 'Amount'], rows: [['EBITDA', fmt.R(v.ebitda)], ['Less: tax paid', fmt.R(-v.taxPaid)], ['Less: working capital investment', fmt.R(-v.workingCapitalInvestment)], ['Less: maintenance capex', fmt.R(-v.maintenanceCapex)], ['Other adjustments', fmt.R(v.otherAdjustments)], ['CFADS', fmt.R(c)]] } };
    },
  },
  dscr: {
    title: 'Debt service cover ratio',
    inputs: [R('cfads', 'CFADS', 5200000), R('interest', 'Scheduled interest', 1600000), R('principal', 'Scheduled principal', 2400000)],
    compute: v => { const ds = v.interest + v.principal, d = div(v.cfads, ds); return { rows: [['Scheduled debt service', fmt.R(ds)], ['DSCR', fmt.x(d)]], verdict: d >= 1 ? 'good' : 'bad', note: 'Below 1.00x, cash flow does not cover scheduled debt service. Any minimum above that is a lender policy choice.' }; },
  },
  'debt-service': {
    title: 'Annual debt service by structure',
    inputs: [R('principal', 'Loan amount', 8000000), P('rate', 'Interest rate (training assumption)', 11.5), N('years', 'Term', 7, 'years'),
      S('structure', 'Repayment structure', [['0', 'Amortising (equal annual payments)'], ['1', 'Equal principal instalments'], ['2', 'Bullet (interest only, principal at maturity)'], ['3', 'Balloon (residual at maturity)']], 0), P('balloonPct', 'Balloon % (balloon structure only)', 30)],
    compute: v => ({ table: scheduleTable(schedule(v.principal, v.rate, Math.max(1, Math.round(v.years)), { structure: Number(v.structure), balloonPct: v.balloonPct })) }),
  },
  'amortisation-schedule': {
    title: 'Amortisation schedule',
    inputs: [R('principal', 'Loan amount', 6400000), P('rate', 'Interest rate (training assumption)', 12), N('years', 'Term', 7, 'years'), N('graceYears', 'Interest-only years at the start', 1, 'years')],
    compute: v => ({ table: scheduleTable(schedule(v.principal, v.rate, Math.max(1, Math.round(v.years)), { grace: Math.min(Math.max(0, Math.round(v.graceYears)), Math.round(v.years) - 1) })) }),
  },
  'loan-sizing': {
    title: 'Loan sizing from a target DSCR',
    inputs: [R('cfads', 'CFADS', 6000000), N('minDscr', 'Minimum DSCR (training assumption)', 1.3, 'x', 0.05), R('existingDebtService', 'Existing annual debt service', 0), P('rate', 'Interest rate (training assumption)', 11.5), N('years', 'Term', 5, 'years')],
    compute: v => {
      const maxDs = div(v.cfads, v.minDscr), cap = maxDs - v.existingDebtService;
      return { rows: [['Maximum annual debt service', fmt.R(maxDs)], ['Capacity for new debt service', fmt.R(cap)], ['Supportable new principal (amortising)', cap > 0 ? fmt.R(pvAnnuity(cap, v.rate, Math.max(1, v.years))) : 'None']], verdict: cap > 0 ? 'good' : 'bad',
        note: 'Sizing is a ceiling, not a target. Check leverage, liquidity headroom and the downside case as well.' };
    },
  },
  'covenant-headroom': {
    title: 'Covenant headroom',
    inputs: [N('actual', 'Actual value', 1.31, '', 0.01), N('threshold', 'Covenant threshold', 1.3, '', 0.01), S('type', 'Covenant type', [['0', 'Minimum (e.g. DSCR, interest cover)'], ['1', 'Maximum (e.g. Debt / EBITDA)']], 0)],
    compute: v => {
      const min = Number(v.type) === 0, h = min ? div(v.actual - v.threshold, v.threshold) : div(v.threshold - v.actual, v.threshold);
      return { rows: [['Headroom', fmt.pct(h * 100)], ['Status', h >= 0 ? 'Passing' : 'In breach']], verdict: h >= 0.15 ? 'good' : 'bad', note: h >= 0 && h < 0.15 ? 'Passing with thin headroom: a small shock could cause a breach.' : undefined };
    },
  },
  'stress-test': {
    title: 'Downside stress test',
    inputs: [R('revenue', 'Base revenue', 86000000), P('grossMarginPct', 'Base gross margin %', 33.5), R('operatingExpenses', 'Operating expenses (excl. depreciation)', 16840000), P('taxRatePct', 'Tax rate %', 27),
      R('taxDeductions', 'Tax-deductible depreciation and interest', 4500000), R('maintenanceCapex', 'Maintenance capex', 2100000), R('workingCapitalInvestment', 'Base working capital investment', 1400000), R('debtService', 'Scheduled debt service', 4882000),
      P('revenueShockPct', 'Revenue shock % (e.g. −10)', -10), P('marginShockPp', 'Gross margin shock (percentage points)', 0), N('extraDebtorDays', 'Extra debtor days', 0, 'days'), P('creditSalesPct', 'Credit sales % of revenue', 60)],
    compute: v => {
      const run = (rev, gm, dd) => {
        const ebitda = rev * gm / 100 - v.operatingExpenses;
        const tax = Math.max(0, (ebitda - v.taxDeductions) * v.taxRatePct / 100);
        const wc = v.workingCapitalInvestment + rev * v.creditSalesPct / 100 * dd / 365;
        const cfads = ebitda - tax - wc - v.maintenanceCapex;
        return { ebitda, cfads, dscr: div(cfads, v.debtService) };
      };
      const b = run(v.revenue, v.grossMarginPct, 0), s = run(v.revenue * (1 + v.revenueShockPct / 100), v.grossMarginPct + v.marginShockPp, v.extraDebtorDays);
      return { table: { headers: ['Measure', 'Base', 'Stressed'], rows: [['EBITDA', fmt.R(b.ebitda), fmt.R(s.ebitda)], ['CFADS', fmt.R(b.cfads), fmt.R(s.cfads)], ['DSCR', fmt.x(b.dscr), fmt.x(s.dscr)]] }, verdict: s.dscr >= 1 ? 'good' : 'bad',
        note: 'Operating expenses are held flat in the stress: fixed costs rarely fall as fast as revenue.' };
    },
  },
  'security-coverage': {
    title: 'Security coverage (forced-sale basis)',
    inputs: [R('facility', 'Facility amount', 6400000), R('asset1Value', 'Asset 1 value', 6400000), P('asset1Haircut', 'Asset 1 haircut %', 50), R('asset2Value', 'Asset 2 value', 1250000), P('asset2Haircut', 'Asset 2 haircut %', 64),
      R('asset3Value', 'Asset 3 value', 0), P('asset3Haircut', 'Asset 3 haircut %', 0), R('priorRanking', 'Claims ranking ahead of this lender', 0)],
    compute: v => {
      const net = [[v.asset1Value, v.asset1Haircut], [v.asset2Value, v.asset2Haircut], [v.asset3Value, v.asset3Haircut]].reduce((s, [a, h]) => s + a * (1 - h / 100), 0) - v.priorRanking;
      const cover = div(net, v.facility);
      return { rows: [['Net realisable security value', fmt.R(net)], ['Security cover', fmt.x(cover)], [net >= v.facility ? 'Surplus' : 'Shortfall', fmt.R(Math.abs(net - v.facility))]], verdict: cover >= 1 ? 'good' : 'bad',
        note: 'Book value is not recoverable value. Realisation takes time and costs money, and enforceability depends on correct legal documentation.' };
    },
  },
};

function parseNum(text) {
  if (typeof text === 'number') return text;
  const cleaned = String(text).replace(/[R,\s%x]/gi, '').replace(/−/g, '-');
  return cleaned === '' || cleaned === '-' ? NaN : Number(cleaned);
}

function SectionHead({ icon, title, badge }) {
  return (
    <div className="section-label-row">
      <span className="section-label-icon">{icon}</span>
      <span className="section-label-title">{title}</span>
      {badge && <span className="section-label-badge">{badge}</span>}
    </div>
  );
}

function ResultTable({ table }) {
  return (
    <div className="comparison-table-wrap lt-result-table">
      <table className="comparison-table">
        <thead><tr>{table.headers.map((h, i) => <th key={i} scope="col">{h}</th>)}</tr></thead>
        <tbody>{table.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{j === 0 ? <strong>{c}</strong> : c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export function CalculatorBlock({ section }) {
  const def = CALCULATORS[section.calculator];
  const initial = useMemo(() => {
    if (!def) return {};
    return Object.fromEntries(def.inputs.map(i => [i.key, section.defaults?.[i.key] ?? i.def]));
  }, [def, section.defaults]);
  const [raw, setRaw] = useState(() => Object.fromEntries(Object.entries(initial).map(([k, v]) => [k, String(v)])));
  if (!def) return null;
  const values = Object.fromEntries(def.inputs.map(i => [i.key, i.options ? Number(raw[i.key]) : (Number.isFinite(parseNum(raw[i.key])) ? parseNum(raw[i.key]) : 0)]));
  const out = def.compute(values, initial);
  const idBase = `calc-${section.id}`;
  return (
    <div className="lt-card lt-calc">
      <SectionHead icon={<Calculator size={18} strokeWidth={1.75} />} title={section.title || def.title} badge="Calculator" />
      {section.prompt && <p className="lt-prompt">{section.prompt}</p>}
      <div className="lt-calc-grid">
        {def.inputs.map(i => (
          <div className="lt-field" key={i.key}>
            <label htmlFor={`${idBase}-${i.key}`}>{i.label}{i.unit && !i.options ? <span className="lt-unit"> ({i.unit === 'R' ? 'R' : i.unit})</span> : null}</label>
            {i.options ? (
              <select id={`${idBase}-${i.key}`} value={raw[i.key]} onChange={e => setRaw(r => ({ ...r, [i.key]: e.target.value }))}>
                {i.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : def.slider && i.unit === 'days' ? (
              <div className="lt-slider-row">
                <input type="range" id={`${idBase}-${i.key}`} min={i.min} max={i.max} step={1} value={values[i.key]} onChange={e => setRaw(r => ({ ...r, [i.key]: e.target.value }))} />
                <output htmlFor={`${idBase}-${i.key}`}>{values[i.key]}</output>
              </div>
            ) : (
              <input id={`${idBase}-${i.key}`} inputMode="decimal" value={raw[i.key]} onChange={e => setRaw(r => ({ ...r, [i.key]: e.target.value }))} />
            )}
          </div>
        ))}
      </div>
      <div className={`lt-calc-out ${out.verdict ? 'lt-calc-out--' + out.verdict : ''}`} aria-live="polite">
        {out.table && <ResultTable table={out.table} />}
        {out.rows && (
          <dl className="lt-out-rows">
            {out.rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
          </dl>
        )}
        {out.note && <p className="lt-note">{out.note}</p>}
      </div>
      <button type="button" className="btn btn-ghost lt-reset" onClick={() => setRaw(Object.fromEntries(Object.entries(initial).map(([k, v]) => [k, String(v)])))}>Reset to example values</button>
    </div>
  );
}

const UNIT_LABEL = { R: 'R', '%': '%', x: 'x', days: 'days', units: 'units', years: 'years', '': '' };

function numericCorrect(answer, tolerance, text) {
  const n = parseNum(text);
  return Number.isFinite(n) && Math.abs(n - answer) <= (tolerance ?? 0);
}

export function PracticeBlock({ section }) {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [showWorking, setShowWorking] = useState(false);
  const id = `practice-${section.id}`;
  const check = () => { const ok = numericCorrect(section.answer, section.tolerance, text); setResult(ok ? 'right' : 'wrong'); if (ok) setShowWorking(true); };
  return (
    <div className="lt-card lt-practice">
      <SectionHead icon={<Target size={18} strokeWidth={1.75} />} title={section.title || 'Practice'} badge="Practice question" />
      <p className="lt-question">{section.question}</p>
      <div className="lt-answer-row">
        <label htmlFor={id} className="lt-sr">Your answer</label>
        {section.unit === 'R' && <span className="lt-affix">R</span>}
        <input id={id} inputMode="decimal" value={text} onChange={e => { setText(e.target.value); setResult(null); }} onKeyDown={e => { if (e.key === 'Enter') check(); }} placeholder="Your answer" />
        {section.unit && section.unit !== 'R' && <span className="lt-affix">{UNIT_LABEL[section.unit]}</span>}
        <button type="button" className="btn btn-primary" onClick={check} disabled={!text.trim()}>Check</button>
      </div>
      <div aria-live="polite">
        {result === 'right' && <p className="lt-feedback lt-feedback--right"><CheckCircle2 size={16} /> Correct.</p>}
        {result === 'wrong' && <p className="lt-feedback lt-feedback--wrong"><XCircle size={16} /> Not quite. {section.hint || 'Check your working and try again.'}</p>}
      </div>
      {(result || showWorking) && (
        <div className="lt-working">
          {!showWorking ? <button type="button" className="btn btn-ghost" onClick={() => setShowWorking(true)}>Show the working</button> : (
            <>
              <p className="lt-working-label">Working</p>
              <p>{section.working}</p>
              {section.interpretation && <p className="lt-interpret">{section.interpretation}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const QUALITY = { best: 'Strongest choice', acceptable: 'Defensible, with gaps', weak: 'Weak choice' };

export function DecisionBlock({ section }) {
  const [choice, setChoice] = useState(null);
  return (
    <div className="lt-card lt-decision">
      <SectionHead icon={<Scale size={18} strokeWidth={1.75} />} title={section.title || 'Decision'} badge="Mini case" />
      {section.scenario && <p className="lt-scenario">{section.scenario}</p>}
      <p className="lt-question">{section.question}</p>
      <div className="lt-options" role="group" aria-label={section.question}>
        {section.options.map((o, i) => (
          <button type="button" key={i} aria-pressed={choice === i} className={`lt-option ${choice === i ? 'lt-option--chosen' : ''} ${choice !== null ? 'lt-option--' + o.quality : ''}`} onClick={() => setChoice(i)}>
            <span className="lt-option-letter">{String.fromCharCode(65 + i)}</span>
            <span>{o.text}</span>
          </button>
        ))}
      </div>
      {choice !== null && (
        <div className="lt-decision-feedback" aria-live="polite">
          {section.options.map((o, i) => (
            <div key={i} className={`lt-fb lt-fb--${o.quality} ${i === choice ? 'lt-fb--chosen' : ''}`}>
              <strong>{String.fromCharCode(65 + i)}. {QUALITY[o.quality] || ''}{i === choice ? ' (your choice)' : ''}</strong>
              <p>{o.feedback}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ClassifyBlock({ section }) {
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState(false);
  const score = section.items.filter((it, i) => answers[i] === it.answer).length;
  return (
    <div className="lt-card lt-classify">
      <SectionHead icon={<ListChecks size={18} strokeWidth={1.75} />} title={section.title || 'Classify'} badge={`${section.items.length} items`} />
      {section.instructions && <p className="lt-prompt">{section.instructions}</p>}
      <ol className="lt-classify-list">
        {section.items.map((it, i) => {
          const state = checked ? (answers[i] === it.answer ? 'right' : 'wrong') : '';
          return (
            <li key={i} className={`lt-classify-item ${state ? 'lt-classify-item--' + state : ''}`}>
              <label htmlFor={`cls-${section.id}-${i}`}>{it.text}</label>
              <select id={`cls-${section.id}-${i}`} value={answers[i] || ''} onChange={e => { setAnswers(a => ({ ...a, [i]: e.target.value })); setChecked(false); }}>
                <option value="">Choose…</option>
                {section.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {checked && (
                <p className="lt-classify-exp">
                  {state === 'right' ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {state === 'right' ? '' : `Best answer: ${it.answer}. `}{it.explanation}
                </p>
              )}
            </li>
          );
        })}
      </ol>
      <div className="lt-classify-actions">
        <button type="button" className="btn btn-primary" disabled={Object.keys(answers).length < section.items.length} onClick={() => setChecked(true)}>Check answers</button>
        {checked && <span className="lt-score" aria-live="polite">{score} of {section.items.length} match the suggested answer</span>}
      </div>
    </div>
  );
}

export function ReflectionBlock({ section, ctx }) {
  const key = `okiru-reflection:${ctx?.userId || 'guest'}:${ctx?.lessonId || ''}:${section.id}`;
  const [text, setText] = useState(() => { try { return localStorage.getItem(key) || ''; } catch { return ''; } });
  const [saved, setSaved] = useState(false);
  const save = () => { try { localStorage.setItem(key, text); setSaved(true); } catch { setSaved(false); } };
  return (
    <div className="lt-card lt-reflection">
      <SectionHead icon={<NotebookPen size={18} strokeWidth={1.75} />} title={section.title || 'Reflection'} badge="Your notes" />
      <label htmlFor={`refl-${section.id}`} className="lt-prompt">{section.prompt}</label>
      <textarea id={`refl-${section.id}`} rows={5} value={text} placeholder={section.placeholder || 'Write your answer here'} onChange={e => { setText(e.target.value); setSaved(false); }} />
      <div className="lt-classify-actions">
        <button type="button" className="btn btn-ghost" onClick={save} disabled={!text.trim()}>Save note</button>
        <span className="lt-note" aria-live="polite">{saved ? 'Saved in this browser.' : 'Notes are kept in this browser only; copy anything you need to keep.'}</span>
      </div>
    </div>
  );
}

export function DownloadBlock({ section }) {
  return (
    <div className="lt-card lt-download">
      <SectionHead icon={<Download size={18} strokeWidth={1.75} />} title={section.title || 'Downloads'} />
      <ul className="lt-download-list">
        {section.files.map(f => (
          <li key={f.href}>
            <a href={f.href} download>
              <span className="lt-format">{f.format}</span>
              <span className="lt-download-text"><strong>{f.label}</strong>{f.description && <span>{f.description}</span>}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FormulaBlock({ section }) {
  return (
    <div className="lt-card lt-formula">
      <SectionHead icon={<Sigma size={18} strokeWidth={1.75} />} title={section.title || 'Formula'} />
      <p className="lt-formula-text">{section.formula}</p>
      {section.note && <p className="lt-note">{section.note}</p>}
    </div>
  );
}

export function WorkedBlock({ section }) {
  return (
    <div className="lt-card lt-worked">
      <SectionHead icon={<ClipboardCheck size={18} strokeWidth={1.75} />} title={section.title || 'Worked example'} badge="Worked example" />
      {section.given?.length > 0 && (
        <dl className="lt-out-rows lt-given">
          {section.given.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
        </dl>
      )}
      {section.steps?.length > 0 && (
        <ol className="lt-worked-steps">
          {section.steps.map((s, i) => (
            <li key={i}><strong>{s.label}</strong><span className="lt-working-line">{s.working}</span>{s.result && <span className="lt-result">= {s.result}</span>}</li>
          ))}
        </ol>
      )}
      {section.answer && <p className="lt-worked-answer">{section.answer}</p>}
      {section.interpretation && <p className="lt-interpret">{section.interpretation}</p>}
    </div>
  );
}

export function DiagnosticBlock({ section }) {
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const qs = section.questions || [];
  const correct = qs.filter(q => answers[q.id] === q.correct).length;
  const score = qs.length ? Math.round(correct / qs.length * 100) : 0;
  const band = [...(section.bands || [])].sort((a, b) => b.min - a.min).find(b => score >= b.min);
  return (
    <div className="lt-card lt-diagnostic">
      <SectionHead icon={<ClipboardCheck size={18} strokeWidth={1.75} />} title={section.title || 'Diagnostic'} badge={`${qs.length} questions · not graded`} />
      {section.instructions && <p className="lt-prompt">{section.instructions}</p>}
      {!done ? (
        <>
          {qs.map((q, qi) => (
            <fieldset key={q.id} className="lt-diag-q">
              <legend><span className="quiz-q-num">Q{qi + 1}.</span> {q.question}</legend>
              <div className="quiz-options">
                {q.options.map((o, oi) => (
                  <button type="button" key={oi} aria-pressed={answers[q.id] === oi} className={`quiz-option ${answers[q.id] === oi ? 'quiz-option--selected' : ''}`} onClick={() => setAnswers(a => ({ ...a, [q.id]: oi }))}>
                    <span className="quiz-opt-letter">{String.fromCharCode(65 + oi)}</span>{o}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}
          <button type="button" className="btn btn-primary" disabled={Object.keys(answers).length < qs.length} onClick={() => setDone(true)}>See my result</button>
        </>
      ) : (
        <div aria-live="polite">
          <p className="lt-diag-score">{correct} of {qs.length} correct ({score}%)</p>
          {band && <div className="lt-diag-band"><strong>{band.title}</strong><p>{band.message}</p></div>}
          <ReviewList items={qs.filter(q => answers[q.id] !== q.correct).map(q => ({ question: q.question, correctAnswer: q.options[q.correct], yourAnswer: q.options[answers[q.id]], explanation: q.explanation }))} />
          <button type="button" className="btn btn-ghost" onClick={() => { setAnswers({}); setDone(false); }}>Retake the diagnostic</button>
        </div>
      )}
    </div>
  );
}

export function ReviewList({ items }) {
  if (!items.length) return null;
  return (
    <div className="lt-review">
      <p className="lt-working-label">Review these</p>
      {items.map((w, i) => (
        <div className="lt-review-item" key={i}>
          <p className="lt-review-q">{w.question}</p>
          {w.yourAnswer !== undefined && <p><span className="lt-tag lt-tag--wrong">Your answer</span> {w.yourAnswer === '' || w.yourAnswer === undefined ? 'No answer' : w.yourAnswer}</p>}
          <p><span className="lt-tag lt-tag--right">Correct</span> {w.correctAnswer}</p>
          {w.explanation && <p className="lt-interpret">{w.explanation}</p>}
        </div>
      ))}
    </div>
  );
}

export function RegulatoryBlock({ section }) {
  if (!section.content) return null;
  return (
    <aside className="lt-card lt-regulatory" aria-label={section.title}>
      <SectionHead icon={<ShieldCheck size={18} strokeWidth={1.75} />} title={section.title} badge="Technical note" />
      {String(section.content).split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
      <p className="lt-reg-meta">
        {section.jurisdiction} · Last reviewed {section.lastReviewed} · Next review due {section.nextReviewDue}{section.reviewOwner ? ` · Owner: ${section.reviewOwner}` : ''}
      </p>
    </aside>
  );
}

export const LESSON_TOOL_TYPES = {
  calculator: CalculatorBlock, practice: PracticeBlock, decision: DecisionBlock, classify: ClassifyBlock, reflection: ReflectionBlock,
  download: DownloadBlock, formula: FormulaBlock, worked: WorkedBlock, diagnostic: DiagnosticBlock, regulatory: RegulatoryBlock,
};

export { CALCULATORS, parseNum, numericCorrect };
